import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  ButtonBase,
  EmptyState,
  Field,
  FormLabel,
  Link,
  ModalDialogBody,
  ModalDialogHeader,
  ModalDialogRoot,
  ServiceIcon,
  Spinner,
  Tag,
  Text,
  TextInput,
  Tooltip,
  TooltipFocusableTrigger,
  VisuallyHidden,
} from "@zapier/design-system";

import { Icon } from "@zapier/zinnia-icons";

import styles from "./AddConnectionModal.module.css";

import { debounce, PLACEHOLDER_APP_ICON, useNotification } from "@zapier/utils";
import {
  App,
  useControlledAppAccess,
  useHydrateImplementationsWithImplementationsMeta,
  useQueryDomainFilterGuards,
  useQueryImplementationsMeta,
  useQueryManagedGuards,
  useRefetchAuthentications,
  useRefetchPublicAndPrivateApps,
  useSplitTreatments,
  useUserAccountRole,
} from "@zapier/client";
import {
  ManagedConnectionSource,
  useEmitManagedConnectionCreateAbandonedEvent,
  useEmitManagedConnectionCreatedEvent,
} from "@zapier/shared";
import { useTranslations } from "@zapier/i18n";
import useCreateAppConnection from "../useCreateAppConnection";
import { ConnectionErrorCallbackPayload } from "../types";
import {
  NewManagedConnection,
  NewManagedConnectionStep,
} from "./NewManagedConnectionStep";

interface AddConnectionModalProps {
  setIsOpen: (isOpen: boolean) => void;
  isOpen: boolean;
  mode?: "personal" | "managed";
  onConnectionError?: (error: ConnectionErrorCallbackPayload) => void;
  // When set, seeds the "Select app" search-and-list step with this term so
  // it opens straight to the matched app instead of an empty list. Does not
  // auto-connect - the user still has to click the row like any other entry
  // point into the managed-connection flow.
  preselectedAppName?: string;
  // Required for mode="managed" — attributes created/abandoned events.
  source?: ManagedConnectionSource;
}

const AddConnectionModal = ({
  setIsOpen,
  isOpen,
  mode = "personal",
  onConnectionError,
  preselectedAppName,
  source,
}: AddConnectionModalProps) => {
  const t = useTranslations("add-connection-modal");
  const [appSearch, setAppSearch] = useState("");
  const [connectingAppId, setConnectingAppId] = useState<string | undefined>();
  const [newManagedConnection, setNewManagedConnection] = useState<
    NewManagedConnection | undefined
  >();

  const { emit: emitManagedConnectionCreated } =
    useEmitManagedConnectionCreatedEvent();
  const { emit: emitManagedConnectionCreateAbandoned } =
    useEmitManagedConnectionCreateAbandonedEvent();
  // Prevents a completed close from also firing an abandoned event.
  const hasCompletedManagedConnectionRef = useRef(false);

  // Reset all step/search/selection state on every open so a prior session's
  // search term, results, and step don't linger when the modal is reopened.
  // When a preselectedAppName is provided, seed the search with it so the
  // implementation-meta lookup below starts immediately.
  useEffect(() => {
    if (!isOpen) return;
    setAppSearch(preselectedAppName ?? "");
    setConnectingAppId(undefined);
    setNewManagedConnection(undefined);
    hasCompletedManagedConnectionRef.current = false;
  }, [isOpen, preselectedAppName]);

  // Single choke point for every way this dialog can close. By the time
  // newManagedConnection is set, the connection already exists server-side
  // (step 1 returned an id) — closing without sharing is still a creation,
  // not an abandonment, since nothing deletes that connection.
  const handleClose = useCallback(
    (open: boolean) => {
      if (
        !open &&
        mode === "managed" &&
        source &&
        !hasCompletedManagedConnectionRef.current
      ) {
        hasCompletedManagedConnectionRef.current = true;
        if (newManagedConnection) {
          emitManagedConnectionCreated(source, { isShared: false });
        } else {
          emitManagedConnectionCreateAbandoned(source);
        }
      }
      setIsOpen(open);
    },
    [
      mode,
      source,
      newManagedConnection,
      emitManagedConnectionCreated,
      emitManagedConnectionCreateAbandoned,
      setIsOpen,
    ],
  );

  const { isAdmin, isSuperAdmin, isAccountOwner } = useUserAccountRole();
  const canManageConnections = isAdmin || isSuperAdmin || isAccountOwner;

  const { isManagedConnectionsFilterEnabled } = useSplitTreatments();

  // Blocking members from connecting to managed apps is gated behind
  // assetmanagement_managed_connections_filter's staged rollout, unlike the
  // compliance status label in ConnectionRow/StatusCell, which must always be
  // accurate. Guard data is still fetched unconditionally (see
  // useQueryManagedGuards), but its loading/error/managed states are only
  // surfaced here once an account has been rolled onto the flag.
  const {
    isAppManaged: isAppManagedRaw,
    isLoading: isGuardsLoadingRaw,
    isError: isGuardsErrorRaw,
  } = useQueryManagedGuards();
  const isAppManaged = useCallback(
    (appId: string) =>
      isManagedConnectionsFilterEnabled && isAppManagedRaw(appId),
    [isManagedConnectionsFilterEnabled, isAppManagedRaw],
  );
  const isGuardsLoading =
    isManagedConnectionsFilterEnabled && isGuardsLoadingRaw;
  const isGuardsError = isManagedConnectionsFilterEnabled && isGuardsErrorRaw;

  // Domain restriction is an additive, non-blocking indicator: a failed
  // guards/types fetch intentionally surfaces no badge rather than erroring,
  // so isLoading/isError are deliberately unobserved here.
  const { getDomainRestriction } = useQueryDomainFilterGuards();

  // Apps the current user can't connect, from the app allowlist/denylist
  // ("controlled apps") including per-user and team exceptions. Distinct from
  // the domain_filter guard above — this blocks creating a connection to the
  // app at all, rather than restricting which OAuth callback domains it can use.
  // This allowlist/denylist model is being migrated to a new
  // open/standard/managed/blocked control-level model:
  // https://gitlab.com/zapier/engineering-index/-/merge_requests/2136
  // Revisit this restricted-row treatment once that ships.
  const { isAppRestricted, isLoading: isControlledAppAccessUnknown } =
    useControlledAppAccess({ enabled: isOpen });

  const { createNotification } = useNotification();

  useEffect(() => {
    if (isGuardsError && isOpen) {
      createNotification({
        message: t("managed.error"),
        type: "error",
      });
    }
  }, [isGuardsError, isOpen, createNotification, t]);

  const { implementationsMeta, isLoading: isImplementationsMetaLoading } =
    useQueryImplementationsMeta({
      term: appSearch,
    });

  const { refetchAuthentications } = useRefetchAuthentications();
  const { refetchPublicAndPrivateApps } = useRefetchPublicAndPrivateApps();

  const onAppSearch = useMemo(
    () =>
      debounce((inputValue: string) => {
        setAppSearch(inputValue || "");
      }, 300),
    [setAppSearch],
  );

  const { hydratedImplementationMetas, isHydrating } =
    useHydrateImplementationsWithImplementationsMeta({
      searchedImplementationMetas: implementationsMeta,
      onlyAppsWithAuth: true,
    });

  const createNewConnection = useCreateAppConnection();

  const managedAppOptions = useMemo(() => {
    // useQueryImplementationsMeta disables its query when appSearch is
    // empty but keeps the last-fetched data (keepPreviousData); the list
    // stays visible, so it must not render that stale leftover.
    if (!appSearch) return [];
    return hydratedImplementationMetas;
  }, [appSearch, hydratedImplementationMetas]);

  const connectListApp = useCallback(
    async (app: (typeof managedAppOptions)[number]) => {
      const appId = String(app.id ?? "");
      const listApp = app as unknown as App;
      setConnectingAppId(appId);
      const result = await createNewConnection(
        listApp,
        mode === "managed" ? { isManaged: true } : undefined,
      );
      setConnectingAppId(undefined);

      if (result.connectionError) {
        onConnectionError?.({
          appId,
          errorDescription: result.connectionError.errorDescription,
        });
        handleClose(false);
        return;
      }

      // `success` is the authoritative signal, not `id` — some OAuth apps
      // never return one even on a genuine success (see
      // useCreateAppConnection). Only advancing to the managed rename/share
      // step actually needs the id; a successful-but-id-less connect should
      // still refetch and notify rather than close with no feedback.
      if (result.success) {
        refetchAuthentications();
        refetchPublicAndPrivateApps();
        if (mode === "managed" && result.id) {
          // Advances to the rename/share step within this same modal — do
          // not close here, that would end the flow before it's configured.
          setNewManagedConnection({
            id: Number(result.id),
            appName: app.name || "",
            appImage: app.image,
          });
          return;
        }
        if (mode === "managed" && source) {
          // No id means no rename/share step to advance to — already complete.
          hasCompletedManagedConnectionRef.current = true;
          emitManagedConnectionCreated(source, { isShared: false });
        }
        createNotification({
          message: t("notifications.connection-added-success", {
            appName: app.name || "",
          }),
          type: "success",
        });
        setIsOpen(false);
        return;
      }
      handleClose(false);
    },
    [
      createNewConnection,
      createNotification,
      emitManagedConnectionCreated,
      handleClose,
      mode,
      onConnectionError,
      refetchAuthentications,
      refetchPublicAndPrivateApps,
      setIsOpen,
      source,
      t,
    ],
  );

  const renderAppList = () => {
    if (isImplementationsMetaLoading || isHydrating) {
      return (
        <div className={styles.managedAppListLoading}>
          <Spinner size="medium" />
        </div>
      );
    }

    if (!appSearch) return null;

    if (managedAppOptions.length === 0) {
      return <EmptyState size="minimal" title={t("empty-state.no-apps")} />;
    }

    return managedAppOptions.map((app) => {
      const appId = String(app.id ?? "");
      const appName = app.name || "";

      // Account-level allowlist/denylist restriction — applies regardless of
      // whether the resulting connection would be personal or managed.
      if (isAppRestricted(appId)) {
        const tooltipText = t("restricted-app.tooltip");
        return (
          <div key={appId} className={styles.managedAppRowRestricted}>
            <Tooltip content={tooltipText} allowMultiline placement="top">
              <TooltipFocusableTrigger
                className={styles.managedAppRowRestrictedInfo}
              >
                <span className={styles.managedAppRowRestrictedApp}>
                  <ServiceIcon
                    serviceName={appName}
                    size="x-small"
                    src={app.image || PLACEHOLDER_APP_ICON}
                  />
                  <Text type="Body3" tag="span">
                    {appName}
                  </Text>
                </span>
                <Icon
                  name="actionLock"
                  size={16}
                  color="GrayWarm6"
                  aria-hidden={true}
                />
              </TooltipFocusableTrigger>
            </Tooltip>
            {canManageConnections && (
              <Link
                href="/app/admin/app-permissions"
                target="_blank"
                variant="tertiary"
                size="compact"
                iconPosition="end"
              >
                {t("buttons.manage-apps")}
                <Icon name="arrowOffsite" aria-hidden={true} />
              </Link>
            )}
          </div>
        );
      }

      // Personal mode only: members can't create a personal connection to an
      // app that's managed-only. Managed mode has no equivalent restriction —
      // that's exactly what it's for.
      const isManagedOnlyForMember =
        mode === "personal" &&
        !canManageConnections &&
        (isAppManaged(appId) || isGuardsLoading);

      if (isManagedOnlyForMember) {
        return (
          <div key={appId} className={styles.managedAppRowRestricted}>
            <Tooltip
              content={t("managed.tooltip")}
              allowMultiline
              placement="top"
            >
              <TooltipFocusableTrigger
                className={styles.managedAppRowRestrictedInfo}
              >
                <span className={styles.managedAppRowRestrictedApp}>
                  <ServiceIcon
                    serviceName={appName}
                    size="x-small"
                    src={app.image || PLACEHOLDER_APP_ICON}
                  />
                  <Text type="Body3" tag="span">
                    {appName}
                  </Text>
                </span>
                <Icon
                  name="personSecurity"
                  size={16}
                  color="GrayWarm6"
                  aria-hidden={true}
                />
              </TooltipFocusableTrigger>
            </Tooltip>
          </div>
        );
      }

      // Domain restriction is non-blocking — shown as an informational badge,
      // with the domain list in the row's tooltip.
      const domainRestriction = getDomainRestriction(appId, "create");

      const isConnecting = connectingAppId === appId;

      const row = (
        <ButtonBase
          key={appId}
          variant="tertiary"
          size="large"
          className={styles.managedAppRow}
          isPending={isConnecting}
          isDisabled={
            (connectingAppId !== undefined && connectingAppId !== appId) ||
            isControlledAppAccessUnknown
          }
          // Overrides the name only while connecting, when the spinner replaces the visible text.
          aria-label={isConnecting ? appName : undefined}
          onPress={() => void connectListApp(app)}
        >
          {isConnecting ? (
            <span className={styles.managedAppRowSpinner}>
              {/* aria-label="" silences Spinner's default label; the button's own aria-label above already names it. */}
              <Spinner size="small" aria-label="" />
            </span>
          ) : (
            <span className={styles.managedAppRowContent}>
              <ServiceIcon
                serviceName={appName}
                size="x-small"
                src={app.image || PLACEHOLDER_APP_ICON}
              />
              <Text type="Body3" tag="span">
                {appName}
              </Text>
              {domainRestriction && (
                <Tag variant="simple">
                  <span className={styles.managedBadgeIcon}>
                    <Icon name="miscDomain" aria-hidden={true} />
                  </span>
                  {t("domain-restricted.badge")}
                </Tag>
              )}
            </span>
          )}
        </ButtonBase>
      );

      if (!domainRestriction) return row;

      return (
        <Tooltip
          key={appId}
          content={t("domain-restricted.tooltip", {
            mode: domainRestriction.mode,
            domains: domainRestriction.domains.join(", "),
          })}
          allowMultiline
          placement="top"
        >
          {row}
        </Tooltip>
      );
    });
  };

  // Do not display the modal if isOpen==false
  if (!isOpen) {
    return;
  }

  const headerTitle = t("header.title-list", { mode });

  return (
    <ModalDialogRoot
      isOpen={true}
      onOpenChange={handleClose}
      aria-label={t("header.aria-label", {
        step: newManagedConnection ? "rename" : mode,
      })}
    >
      {newManagedConnection ? (
        <NewManagedConnectionStep
          connection={newManagedConnection}
          onCancel={() => handleClose(false)}
          onComplete={(isShared) => {
            hasCompletedManagedConnectionRef.current = true;
            if (source) {
              emitManagedConnectionCreated(source, { isShared });
            }
            setIsOpen(false);
          }}
        />
      ) : (
        <>
          <ModalDialogHeader textAlign="start">{headerTitle}</ModalDialogHeader>
          <ModalDialogBody textAlign="start" className={styles.modalBody}>
            <div className={styles.appSearch}>
              <Field
                label={t("labels.app")}
                inputId={
                  mode === "managed"
                    ? "add-managed-connection-search"
                    : "add-connection-search"
                }
                renderLabel={(labelProps) => (
                  <VisuallyHidden>
                    <FormLabel {...labelProps}>{t("labels.app")}</FormLabel>
                  </VisuallyHidden>
                )}
                renderInput={(inputProps) => (
                  <TextInput
                    {...inputProps}
                    aria-label={t("labels.app")}
                    placeholder={t("placeholders.search-apps")}
                    defaultValue={preselectedAppName}
                    onChange={(event) => onAppSearch(event.target.value)}
                    renderIconBefore={() => (
                      <Icon name="navSearch" aria-hidden={true} />
                    )}
                    isFullWidth={true}
                    size="medium"
                  />
                )}
              />
            </div>
            <div className={styles.managedAppList}>{renderAppList()}</div>
          </ModalDialogBody>
        </>
      )}
    </ModalDialogRoot>
  );
};

export default AddConnectionModal;
